<template>
  <div v-if="paymentMethod" class="mb-10">
    <Table>
      <TableBody>
        <BillingInvoiceRow>
          <template #head>Payment Method</template>
          <template #cell>
            <span v-if="paymentMethod">
              {{ paymentMethod }}
              <Dialog v-model:open="open" @update:open="onOpen">
                <DialogTrigger>
                  <a href="#"> Change </a>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Update Payment Method</DialogTitle>
                  </DialogHeader>
                  <form
                    id="update-payment-method"
                    class="min-w-72 min-h-72 px-8 py-6"
                    @submit="onSubmit"
                  >
                    <fieldset class="flex flex-col gap-1 mb-8">
                      <Label for="cardName">Name on card</Label>
                      <Input
                        v-bind="cardNameAttrs"
                        id="cardName"
                        v-model="cardName"
                        placeholder="Card name"
                      />
                      <span class="text-error-400">{{ errors?.cardName }}</span>
                    </fieldset>
                    <div
                      id="card-element"
                      style="padding: 0; background-color: transparent"
                    />
                  </form>
                  <DialogFooter>
                    <DialogClose as-child>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        :disabled="isPending"
                      >
                        Cancel
                      </Button>
                    </DialogClose>
                    <Button
                      type="submit"
                      :disabled="isPending"
                      :loading="isPending"
                      form="update-payment-method"
                      size="sm"
                    >
                      Submit
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </span>
            <span v-else>Not set</span>
          </template>
        </BillingInvoiceRow>
        <BillingInvoiceRow v-if="balance">
          <template #head
            >{{ balance < 0 ? 'Credit' : 'Debit' }} Balance</template
          >
          <template #cell>
            <span>${{ (Math.abs(balance) / 100).toFixed(2) }}</span>
          </template>
        </BillingInvoiceRow>
      </TableBody>
    </Table>
    <p v-if="isPastDue" class="text-xs text-error-400 mt-1">
      Your subscription payment failed. Please
      <a
        href="#"
        class="text-error-400 hover:text-error-400 underline"
        @click="openDialog"
        >update your payment method</a
      >
      to avoid service interruption.
    </p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Button } from '@ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
  DialogClose,
  DialogFooter,
} from '@ui/components/dialog';
import { Table, TableBody } from '@/ui/components/table/index.js';
import { loadStripe } from '@stripe/stripe-js';
import requests from '@/vuex/requests.js';
import { Label } from '@ui/components/label';
import { Input } from '@ui/components/input';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { toast } from 'vue-sonner';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import BillingInvoiceRow from './BillingInvoiceRow.vue';

const props = defineProps({
  invoiceDetails: {
    type: Object,
    required: false,
    default: null,
  },
  paymentMethodDetails: {
    type: Object,
    required: false,
    default: null,
  },
  balance: {
    type: Number,
    required: false,
    default: 0,
  },
});

const paymentMethod = computed(() => {
  if (
    props.paymentMethodDetails &&
    props.paymentMethodDetails.type &&
    props.paymentMethodDetails.last_four
  ) {
    return `${props.paymentMethodDetails.type} ending in ${props.paymentMethodDetails.last_four}`;
  }
  return null;
});

const open = ref(false);

const trxIntent = ref(null);
const queryClient = useQueryClient();
/** @type {import('@stripe/stripe-js').Stripe} */
let stripe;
let elements;
let paymentElement;

const initStripe = async ({ intent }) => {
  stripe = await loadStripe(process.env.MIX_STRIPE_KEY);
  elements = stripe.elements({
    clientSecret: intent,
    appearance: {
      disableAnimations: false,
      variables: {
        colorPrimary: '#ffffff',
        colorBackground: '#1a1e1e',
        colorText: '#ffffff',
        colorDanger: '#FF5449',
        fontFamily: 'Open Sans, sans-serif',
        spacingUnit: '2px',
        borderRadius: '4px',
        gridRowSpacing: '32px',
        gridColumnSpacing: '32px',
        // See all possible variables below
      },
      rules: {
        '.Label': {
          padding: '0',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: '4px',
        },
        '.Input': {
          height: '40px',
          padding: '10px 12px',
          backgroundColor: '#2F3637',
          borderColor: '#5E6B6E',
        },
        '.Error': {
          marginTop: '4px',
        },
      },
    },
  });

  paymentElement = elements.create('payment', {
    terms: {
      card: 'never',
    },
    layout: 'auto',
    fields: {
      billingDetails: {
        address: {
          country: 'never',
        },
      },
    },
  });

  // Wait until the card element is mounted (nextTick doesn't always work)
  let i = 0;
  while (i < 3) {
    if (document.getElementById('card-element')) {
      break;
    }
    i += 1;
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => {
      setTimeout(resolve, 500);
    });
  }
  paymentElement.mount('#card-element');
};
const store = useStore();

const isPastDue = computed(() => store.getters['workspace/getIsPastDue']);

const refreshIntent = async () => {
  try {
    const intentRes = await requests.workspaces.subscription.getIntent(
      store.getters['workspace/getId']
    );
    trxIntent.value = intentRes.intent;

    initStripe({ intent: trxIntent.value });
  } catch (error) {
    console.error(error);
  }
};

const onOpen = value => {
  if (!value) {
    return;
  }
  if (!trxIntent.value) {
    refreshIntent();
  } else {
    initStripe({ intent: trxIntent.value });
  }
};

function openDialog() {
  open.value = true;
  setTimeout(() => {
    onOpen(true);
  });
}

const route = useRoute();
onMounted(() => {
  if (route.query.paymentMethod) {
    openDialog();
  }
});

const validationSchema = toTypedSchema(
  z.object({
    cardName: z
      .string()
      .min(1, {
        message: 'The card name field is required',
      })
      .max(255),
  })
);

const { handleSubmit, errors, defineField } = useForm({
  validationSchema,
  initialValues: {
    cardName: '',
  },
});

const [cardName, cardNameAttrs] = defineField('cardName');

const { isPending, mutate } = useMutation({
  mutationFn: async payload => {
    const res = await requests.workspaces.subscription.updatePaymentMethod(
      store.getters['workspace/getId'],
      {
        ...payload,
      }
    );
    return res;
  },
  onSuccess: async () => {
    toast.success('Your payment method was updated');
    await queryClient.invalidateQueries();
    open.value = false;
  },
  onError: async error => {
    console.error(error);
    toast.error(
      error?.response?.data?.title ||
        'Something went wrong, please try again later'
    );
    await refreshIntent();
  },
});

const onSubmit = handleSubmit(async values => {
  const { error, setupIntent } = await stripe.confirmSetup({
    elements,
    confirmParams: {
      payment_method_data: {
        billing_details: {
          name: values.cardName,
          address: {
            country: props.invoiceDetails?.country,
          },
        },
      },
    },
    redirect: 'if_required',
  });

  if (error) {
    console.error(error);
    toast.error(
      error?.message || 'Something went wrong, please try again later'
    );
    return;
  }

  const stripePaymentMethod = setupIntent.payment_method;
  const paymentData = {
    stripePaymentMethod,
  };

  mutate(paymentData);
});
</script>
